import React, { useEffect, useState } from 'react';
import { noop } from 'lodash';
import { I18n } from '@fiverr-private/i18n-react';
import { Modal, ModalCloseBtn, ModalHeader, ModalContent, ModalFooter, useModalContext } from '@fiverr-private/overlay';
import { Stack } from '@fiverr-private/layout_components';
import { Button } from '@fiverr-private/button';
import { Text } from '@fiverr-private/typography';
import { logger } from '@fiverr-private/obs';
import pathfinder from '@fiverr-private/pathfinder';
import { getContext } from '@fiverr-private/fiverr_context';
import { UploadContext, useUploadContext } from '../AttachmentUpload';
import { SupportLink } from '../DocumentLinks';
import { UploadButtonContent } from '../AttachmentUpload/UploadButton';
import { Attachment } from '../AttachmentUpload/types';
import { queryOrganizationSellerData, sendSaveTemplatesRequests } from '../../api';
import { RequestToUploadModal } from '../RequestToUploadModal';
import { BiData, reportEventsApi } from '../../biEvents';

const TRANSLATION_PREFIX = 'legal_documents_package.upload_modal';

export interface UploadModalProps {
    children: React.ReactNode;
    onUploadComplete?: (attachments: Attachment[]) => void;
    isAllowedToUpload?: boolean;
    checkEmptyTemplates?: boolean;
    biData: BiData;
}

export const UploadModal = ({
    children,
    isAllowedToUpload = true,
    checkEmptyTemplates = false,
    biData,
    ...props
}: UploadModalProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [shouldRedirect, setShouldRedirect] = useState(false);

    const checkIfOrgHasLegalDocs = async (): Promise<boolean> => {
        try {
            const { organizationId } = getContext();

            if (!organizationId) {
                return false;
            }

            const response = await queryOrganizationSellerData();

            return response.data?.legalDocsData?.totalTemplates > 0;
        } catch (e) {
            logger.error(e as Error);
            return false;
        }
    };

    useEffect(() => {
        if (!checkEmptyTemplates) return;
        const checkLegalDocs = async () => {
            const hasLegalDocs = await checkIfOrgHasLegalDocs();
            if (!hasLegalDocs) {
                setShouldRedirect(true);
            }
        };
        checkLegalDocs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTriggerClick = () => {
        if (checkEmptyTemplates && shouldRedirect) {
            window?.open(pathfinder('legal_documents_page_render'), '_blank');
            window?.location.reload();
        } else {
            setIsModalOpen(true);
        }
    };

    if (!isAllowedToUpload) {
        return <RequestToUploadModal biData={biData}>{children}</RequestToUploadModal>;
    }

    return (
        <>
            <div onClick={handleTriggerClick}>{children}</div>
            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <UploadContext>
                    <UploadModalContent biData={biData} {...props} />
                </UploadContext>
            </Modal>
        </>
    );
};

export const UploadModalContent = ({
    onUploadComplete = noop,
}: Omit<UploadModalProps, 'children' | 'isAllowedToUpload' | 'pageName'>) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { uploadedFiles, uploadProgress, resetState } = useUploadContext();
    const { close } = useModalContext();

    const onSubmit = async () => {
        try {
            reportEventsApi.uploadDocument({});
            setIsSubmitting(true);
            await sendSaveTemplatesRequests(uploadedFiles);
            close();
            onUploadComplete?.(uploadedFiles);
        } catch (error) {
            logger.error(error as Error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <ModalContent onAnimationEnd={resetState}>
            <ModalHeader>
                <I18n k={`${TRANSLATION_PREFIX}.title`} />
                <ModalCloseBtn />
            </ModalHeader>
            <Stack direction="column" width="100%" paddingX="8" overflow="auto" gap="0">
                <Text paddingBottom="8">
                    <I18n
                        k={`${TRANSLATION_PREFIX}.description`}
                        templates={{
                            link: (text) => <SupportLink text={text as string} />,
                        }}
                    />
                </Text>
                <UploadButtonContent withPreview />
            </Stack>
            <ModalFooter>
                <Button variant="ghost" onClick={close}>
                    <I18n k={`${TRANSLATION_PREFIX}.cancel`} />
                </Button>
                <Button disabled={uploadProgress?.isLoading || isSubmitting} loading={isSubmitting} onClick={onSubmit}>
                    <I18n k={`${TRANSLATION_PREFIX}.submit`} />
                </Button>
            </ModalFooter>
        </ModalContent>
    );
};
